
import {
  Component,
  Provide,
  Mixins,
  Watch,
  Prop
} from "vue-property-decorator";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";
import ExternalAuthMixin from "@/components/app/global/payments/externalAuthMixin.vue";

// Enums/Data
import {
  QUERY_PARAMS,
  BasketTypes,
  BasketEvents,
  CheckoutFlows,
  BrandConfigKeys
} from "@/data/constants";

// Types
import {
  IShopProviderData,
  IShopProviderMethods
} from "@/models/providers/basket/shop";

import { IClient } from "@/models/clients";
import { IAccount } from "@/models/accounts";

@Component
export default class ShopProvider extends Mixins(
  RequestPromises,
  Context,
  ExternalAuthMixin
) {
  // Props

  @Prop({ default: "" })
  public routeClientId!: string | number;

  @Prop({ default: "" })
  public routeAccountId!: string | number;

  @Prop({ default: "" })
  public routeCurrencyCode!: string;

  // Variables

  public adminShopRoute = "adminOrderShop";
  public basketId: BasketTypes | string = BasketTypes.NEW;

  // Life cycle methods

  public async created() {
    this.checkExternalAuthQueryParams();
    this.initialiseEventListeners();
  }

  public beforeDestroy() {
    this.destroyEventListeners();
  }

  // Methods

  public assignToNewBasketShop(client: IClient) {
    this.$router.replace(
      _.merge(
        this.$route.name === this.adminShopRoute
          ? {}
          : { name: this.adminShopRoute },
        {
          query: {
            [QUERY_PARAMS.ACCOUNT_ID]: _.get(client.accounts, "[0].id"),
            [QUERY_PARAMS.CLIENT_ID]: client.id
          }
        }
      )
    );
  }

  public getQuery() {
    const query = _.clone(this.$route.query);
    const regex = new RegExp(
      `^(${[
        QUERY_PARAMS.ACCOUNT_ID,
        QUERY_PARAMS.CLIENT_ID,
        QUERY_PARAMS.PRODUCT_ID,
        QUERY_PARAMS.BASKET_PRODUCT_ID,
        QUERY_PARAMS.BILLING_CYCLE_MONTHS,
        QUERY_PARAMS.QUANTITY,
        QUERY_PARAMS.CURRENCY_CODE
      ].join("|")})$`,
      "gm"
    );
    const matches = _.keys(query)
      .join("\n")
      .match(regex);
    if (!matches || !matches.length) return;
    matches.forEach(key => delete query[key]);
    return query;
  }

  public setBasketId(value: BasketTypes | string) {
    this.basketId = value;
  }

  public initialiseEventListeners() {
    this.$bus.$on(BasketEvents.BASKET_INITIATED, () => {
      if (this.isAdmin && _.get(this.$route, "name") === "adminOrderBasket") {
        this.$router.push({
          name: this.adminShopRoute
        });
      }
    });
  }

  public resetBasket({
    accountId,
    clientId
  }: {
    accountId: IAccount["id"];
    clientId: IClient["id"];
  }) {
    this.goToShop({ accountId, clientId }, { basketId: BasketTypes.NEW });
    this.basketId = BasketTypes.NEW;
  }

  public destroyEventListeners() {
    this.$bus.$off(BasketEvents.BASKET_INITIATED);
  }

  // Redirections

  public goToCheckout() {
    this.$router
      .push({
        name: this.isAdmin ? "adminOrderCheckout" : "orderCheckoutStep"
      })
      .catch(err => err);
  }

  public goToBasket(basketId) {
    if (
      this.$route.name ===
      (this.isAdmin
        ? "adminOrderBasketView"
        : this.isOnePageCheckoutFlow
        ? "orderCheckoutStep"
        : "orderBasketStep")
    )
      return;

    return this.$router
      .push({
        name: this.isAdmin ? "adminOrderBasketView" : "orderBasketStep",
        ...(basketId ? { params: { basketId } } : {})
      })
      .catch(err => err);
  }

  public backToBasketList(): void {
    this.$router.push({ name: "adminBaskets" });
  }

  public goToShop(query = {}, params = {}) {
    this.$router.push({
      name: this.isAdmin ? "adminOrderShop" : "orderShopStep",
      query,
      params
    });
  }

  public goToProductView({
    basketProductId = "",
    productId = "",
    queryParams = {}
  }: {
    basketProductId?: string;
    productId?: string;
    queryParams?: object;
  }) {
    this.$router.push({
      name: this.isAdmin ? "adminOrderProductView" : "orderProductViewStep",
      query: _.merge(
        {},
        basketProductId
          ? { [QUERY_PARAMS.BASKET_PRODUCT_ID]: basketProductId }
          : {},
        productId ? { [QUERY_PARAMS.PRODUCT_ID]: productId } : {},
        queryParams
      )
    });
  }

  public goToAuthenticate() {
    this.$router.push({
      name: "orderAuthenticateStep"
    });
  }

  public goToComplete(query = {}) {
    this.$router.push(
      _.merge({
        name: this.isAdmin ? "adminOrderComplete" : "orderCompleteStep",
        query
      })
    );
  }

  // Computed

  public get isOnePageCheckoutFlow() {
    return [CheckoutFlows.ONE_PAGE].includes(
      this.$store.getters["brand/config"][BrandConfigKeys.CHECKOUT_FLOW]
    );
  }

  public get shopBasketKey() {
    const basketId = this.isAdmin ? this.$route.params["basketId"] : "";

    if (this.routeAccountId && this.routeClientId) {
      return btoa(
        [this.routeAccountId, this.routeClientId, basketId].join(":")
      );
    }
    return basketId;
  }

  // Watcher

  @Watch("$route", { deep: true, immediate: true })
  public checkRoute() {
    if (
      this.basketId === this.$route.query[QUERY_PARAMS.BASKET_ID] &&
      this.basketId !== BasketTypes.NEW &&
      (this.routeAccountId || this.routeClientId || this.routeCurrencyCode)
    ) {
      this.$router.replace({
        query: this.getQuery()
      });
    }
  }

  // Provide

  @Provide("shopProviderData")
  public providerData(): IShopProviderData {
    return {
      isOnePageCheckoutFlow: this.isOnePageCheckoutFlow,
      shopBasketKey: this.shopBasketKey
    };
  }

  @Provide("shopProviderMethods")
  public get providerMethods(): IShopProviderMethods {
    return {
      assignToNewBasketShop: this.assignToNewBasketShop,
      backToBasketList: this.backToBasketList,
      goToAuthenticate: this.goToAuthenticate,
      goToBasket: this.goToBasket,
      goToCheckout: this.goToCheckout,
      goToComplete: this.goToComplete,
      goToProductView: this.goToProductView,
      goToShop: this.goToShop,
      resetBasket: this.resetBasket,
      setBasketId: this.setBasketId
    };
  }

  //  Render

  public render() {
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $shopData: this.providerData(),
        $shopMethods: this.providerMethods
      })
    );
  }
}
